import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Typography,
    Stack,
    Box,
    Grid,
    Skeleton,
    Divider,
} from '../../../../base';
import { reportsSelectors } from '../../../../actions';
import {
    IClientTimeToHireCompanyData,
    TIME_TO_HIRE_METRIC_CARD_CONFIGS,
} from '../../../../common';
import SummaryStatsBar from './SummaryStatsBar';
import MetricDonutCard from './MetricDonutCard';
import TimeToHireEmptyState from '../TimeToHireEmptyState';
import {
    StyledSkeletonCard,
    StyledSkeletonStatsRow,
    StyledSkeletonStatItem,
} from './CompanyWideView.styles';

const CompanyWideView = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const timeToHireData = useAppSelector(reportsSelectors.getTimeToHireData);
    const isLoading = useAppSelector(reportsSelectors.getIsLoadingTimeToHire);

    const data =
        timeToHireData && timeToHireData.view === 'company'
            ? (timeToHireData as IClientTimeToHireCompanyData)
            : null;

    const getDonutPercentage = (metricDays: number | null): number => {
        if (
            metricDays === null ||
            !data?.hiringTimeAverage?.averageDays ||
            data.hiringTimeAverage.averageDays === 0
        ) {
            return 0;
        }
        return Math.round(
            (metricDays / data.hiringTimeAverage.averageDays) * 100
        );
    };

    const renderSkeletons = () => (
        <Stack spacing={3}>
            <StyledSkeletonStatsRow>
                {Array.from({ length: 4 }).map((_, i) => (
                    <StyledSkeletonStatItem key={i}>
                        <Skeleton variant="circular" width={45} height={45} />
                        <Stack>
                            <Skeleton width={140} height={16} />
                            <Skeleton width={80} height={28} />
                        </Stack>
                    </StyledSkeletonStatItem>
                ))}
            </StyledSkeletonStatsRow>
            <Divider sx={{ borderColor: theme.palette.grey[200] }} />
            <Grid container spacing={2}>
                {TIME_TO_HIRE_METRIC_CARD_CONFIGS.map((config) => (
                    <Grid item xs={12} md={4} key={config.dataField}>
                        <StyledSkeletonCard>
                            <Skeleton
                                variant="circular"
                                width={120}
                                height={120}
                            />
                            <Stack spacing={0.5} sx={{ flex: 1 }}>
                                <Skeleton width={120} height={18} />
                                <Skeleton width={180} height={14} />
                                <Skeleton width={100} height={28} />
                                <Skeleton width={160} height={12} />
                            </Stack>
                        </StyledSkeletonCard>
                    </Grid>
                ))}
            </Grid>
        </Stack>
    );

    const renderContent = () => {
        if (!data) return null;

        const totalDays = TIME_TO_HIRE_METRIC_CARD_CONFIGS.reduce(
            (sum, config) =>
                sum + Math.round(data[config.dataField].averageDays ?? 0),
            0
        );

        if (totalDays === 0) return <TimeToHireEmptyState />;

        const totalCount = TIME_TO_HIRE_METRIC_CARD_CONFIGS.reduce(
            (sum, config) => sum + (data[config.dataField].count || 0),
            0
        );

        return (
            <Stack spacing={3}>
                <SummaryStatsBar data={data} />
                <Divider sx={{ borderColor: theme.palette.grey[200] }} />
                <Grid container spacing={2}>
                    {TIME_TO_HIRE_METRIC_CARD_CONFIGS.map((config) => {
                        const metricData = data[config.dataField];
                        const pct = config.isFullTimeline
                            ? 100
                            : getDonutPercentage(metricData.averageDays);
                        const countPct =
                            totalCount > 0
                                ? (metricData.count / totalCount) * 100
                                : 0;
                        const footerText = config.isFullTimeline
                            ? t('reportsPage.timeToHireReport.ofHiringTimeline')
                            : t(
                                  'reportsPage.timeToHireReport.representsPercent',
                                  { percent: pct }
                              );

                        return (
                            <Grid
                                item
                                xs={12}
                                lg={6}
                                xl={4}
                                key={config.dataField}
                            >
                                <MetricDonutCard
                                    title={t(config.titleKey)}
                                    subtitle={t(config.subtitleKey)}
                                    averageDays={metricData.averageDays}
                                    percentage={pct}
                                    countPercentage={countPct}
                                    footerText={footerText}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Stack>
        );
    };

    return (
        <Stack sx={{ padding: theme.spacing(2, 3), flex: 1 }}>
            <Typography variant="h6">
                {t('reportsPage.timeToHireReport.companyWideTitle')}
            </Typography>
            <Typography variant="body1" color={theme.palette.grey[500]}>
                {t('reportsPage.timeToHireReport.companyWideSubtitle')}
            </Typography>
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    flex: 1,
                }}
            >
                {isLoading ? renderSkeletons() : renderContent()}
            </Box>
        </Stack>
    );
};

export default CompanyWideView;
